import createFetchClient, { type Middleware } from "openapi-fetch";
import createClient from "openapi-react-query";
import { QueryClient } from "@tanstack/react-query";
import type { paths } from "./api-schema";
import { getEmbedAuth } from "./auth";
import { setTrackingHeaders } from "./trackingHeaders";

// The one typed client every renderer talks to cortex through. There is no
// token refresh: the only credential is the handshake-delivered API key, so
// an expired key surfaces as the entitlement error state.

export class QueryError extends Error {
  statusCode: number;
  statusText: string;
  method: string;
  url: string;

  constructor(response: Response, method: string) {
    super(`${method} ${response.url} failed with status ${response.status}`);
    this.name = "QueryError";
    this.statusCode = response.status;
    this.statusText = response.statusText;
    this.method = method;
    this.url = response.url;
  }
}

/** The shell's distinct failure modes. */
export type ErrorKind =
  | "unknown-type"
  | "handshake-timeout"
  | "entitlement"
  | "not-found"
  | "network";

/** Map a query failure onto the shell's error states. */
export function errorKind(error: unknown): ErrorKind {
  if (error instanceof QueryError) {
    if (error.statusCode === 401 || error.statusCode === 403) {
      return "entitlement";
    }
    if (error.statusCode === 404) return "not-found";
  }
  // Anything else — fetch TypeError (offline, DNS, CORS), 5xx, timeouts —
  // presents as a retryable network/service failure.
  return "network";
}

// Transient failures worth retrying on idempotent GETs. 401 is deliberately
// absent: there is no refresh path, so retrying an unentitled key just delays
// the honest error state.
const RETRY_STATUS_CODES = [408, 429, 500, 502, 503, 504];

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (failureCount >= 2) return false;
        if (error instanceof QueryError) {
          return RETRY_STATUS_CODES.includes(error.statusCode);
        }
        return true; // network-level failure
      },
    },
  },
});

// In dev, requests go through the Vite proxy (vite.config.ts) so the browser
// sees them as same-origin; production builds call cortex directly
// (VITE_CORTEX_API_URL is validated at build time — see /env.ts). Tests need
// an absolute URL because undici's Request rejects relative ones.
const rawBaseUrl = import.meta.env.TEST
  ? "https://cortex.test"
  : import.meta.env.DEV
    ? ""
    : import.meta.env.VITE_CORTEX_API_URL;

// Trailing slash stripped so `${base}${path}` joins cleanly.
const baseUrl = rawBaseUrl.replace(/\/+$/, "");

/**
 * Base for cortex URLs constructed outside the typed client (e.g. the PDF
 * binary URL handed to pdf.js). "" in dev — relative URLs ride the proxy.
 * Append schema-validated absolute paths (`/api/v1/...`) by concatenation.
 */
export const CORTEX_BASE_URL = baseUrl;

/**
 * The same auth + tracking header set the middleware attaches, as a plain
 * object, for requests that bypass the typed client entirely — pdf.js fetches
 * the PDF binary itself, so these must be passed to it explicitly or the
 * request goes out unauthenticated.
 */
export function authenticatedRequestHeaders(): Record<string, string> {
  const headers = new Headers();
  const auth = getEmbedAuth();
  if (auth) {
    headers.set("X-API-Key", auth.apiKey);
    headers.set("X-Aiera-Parent-Origin", auth.parentOrigin);
  }
  setTrackingHeaders(headers);
  return Object.fromEntries(headers.entries());
}

export const fetchClient = createFetchClient<paths, "application/json">({
  baseUrl,
  // Resolve fetch at call time (openapi-fetch otherwise captures the global
  // at creation), so tests can stub globalThis.fetch after import.
  fetch: (request) => globalThis.fetch(request),
  headers: {
    "Content-Type": "application/json",
  },
});

const middleware: Middleware = {
  onRequest: ({ request }) => {
    const auth = getEmbedAuth();
    if (auth) {
      request.headers.set("X-API-Key", auth.apiKey);
      // The validated embedding origin from the handshake (browser-asserted
      // MessageEvent.origin) — never derived from document.referrer.
      request.headers.set("X-Aiera-Parent-Origin", auth.parentOrigin);
    }

    setTrackingHeaders(request.headers);

    return request;
  },
  onResponse: ({ response, request }) => {
    if (!response.ok) {
      throw new QueryError(response, request.method);
    }
    return response;
  },
};

fetchClient.use(middleware);

export const { useQuery, useSuspenseQuery, queryOptions } =
  createClient(fetchClient);
